import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/api/client';
import { queryKeys } from '@/api/queryClient';
import type { PreviewRequest, PreviewResponse, ApplyRequest, ApplyResponse } from '@/types';

/**
 * Hook to create a preview
 */
export function useCreatePreview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: PreviewRequest) => {
      return api.post<PreviewResponse>(endpoints.preview.create, request);
    },
    onSuccess: (data) => {
      // Cache the preview result
      queryClient.setQueryData(queryKeys.preview.detail(data.previewId), data);
    },
  });
}

/**
 * Hook to fetch an existing preview
 */
export function usePreview(id: string | null) {
  return useQuery({
    queryKey: queryKeys.preview.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Preview ID required');
      return api.get<PreviewResponse>(endpoints.preview.get(id));
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // Preview data is valid for 5 minutes
  });
}

/**
 * Hook to approve a preview
 */
export function useApprovePreview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (previewId: string) => {
      return api.post<{ message: string; previewId: string; approvedAt: string }>(
        endpoints.preview.approve(previewId)
      );
    },
    onSuccess: (_, previewId) => {
      // Invalidate preview query to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.preview.detail(previewId) });
    },
  });
}

/**
 * Hook to delete a preview
 */
export function useDeletePreview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (previewId: string) => {
      return api.delete<{ message: string }>(`/api/v1/preview/${previewId}`);
    },
    onSuccess: (_, previewId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.preview.detail(previewId) });
    },
  });
}

/**
 * Hook to apply changes from a preview
 */
export function useApplyChanges() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ApplyRequest) => {
      return api.post<ApplyResponse>(endpoints.apply, request);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}
